/**
 * ui-sounds — short non-musical confirmation tones for graded answers.
 *
 * Deliberately distinct from the practice content:
 *   - Sine waves (the piano samples are harmonic-rich; sines stand apart)
 *   - Very brief (~120ms) so they don't bleed into "what did I just hear?"
 *   - Pitches outside the 48-76 MIDI range used by exercises
 *   - Low gain so they're a nudge, not a fanfare
 *
 * The audio context is lazy-init on first play, which always happens
 * inside a user-gesture handler (answering an exercise). Browsers block
 * AudioContext creation outside user gestures, so we never try.
 *
 * Implementation lives in this app, not the audio-engine package, because
 * the audio engine is musical-content-only and shouldn't be hijacked for
 * UI feedback (which would compete for engine state, latency budget, etc.).
 */

export type FeedbackKind = "correct" | "wrong";

export interface UISoundPlayer {
  play(kind: FeedbackKind): void;
  destroy(): void;
}

/**
 * Tone-spec describes the synthesis of one feedback chime. Exported for
 * tests so we can assert correct vs wrong are perceptually distinct
 * without having to spin up an AudioContext.
 */
export interface ToneSpec {
  /** Frequencies (Hz) layered simultaneously. Sine voices. */
  frequencies: number[];
  /** Total duration in seconds (envelope completes here). */
  durationSec: number;
  /** Peak gain per voice (post-attack, before decay). */
  gain: number;
}

export const TONE_SPECS: Record<FeedbackKind, ToneSpec> = {
  // Correct: gentle major-third ding, octave above singing range so it
  // doesn't read as a musical answer. Two voices = subtle harmony.
  correct: {
    frequencies: [880, 1108.73], // A5 + C#6 (M3)
    durationSec: 0.18,
    gain: 0.06,
  },
  // Wrong: low minor-second buzz — slight beat between the two voices,
  // dropped well below the practice register so it can't be confused
  // with a tonic.
  wrong: {
    frequencies: [196, 207.65], // G3 + Ab3 (m2)
    durationSec: 0.22,
    gain: 0.05,
  },
};

/**
 * Schedule a single tone on a ready AudioContext at `startTime`.
 * Pure-ish: no globals, no DOM. Pulled out so tests can drive it
 * against a fake context and assert nodes are wired correctly.
 */
export function scheduleTone(
  ctx: AudioContext,
  destination: AudioNode,
  spec: ToneSpec,
  startTime: number,
): void {
  const { frequencies, durationSec, gain } = spec;
  const attackSec = 0.01;
  const releaseSec = Math.max(0.04, durationSec - attackSec);
  for (const freq of frequencies) {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, startTime);
    env.gain.linearRampToValueAtTime(gain, startTime + attackSec);
    env.gain.exponentialRampToValueAtTime(0.0001, startTime + attackSec + releaseSec);
    osc.connect(env).connect(destination);
    osc.start(startTime);
    osc.stop(startTime + attackSec + releaseSec + 0.02);
  }
}

/**
 * Create a UI sound player. Lazy-init pattern: the first play() builds
 * the AudioContext (which must happen inside a user gesture), subsequent
 * calls reuse it.
 */
export function createUISoundPlayer(): UISoundPlayer {
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;

  return {
    play(kind) {
      try {
        if (!ctx) {
          const Ctor =
            (window.AudioContext as typeof AudioContext | undefined) ??
            ((window as unknown as { webkitAudioContext?: typeof AudioContext })
              .webkitAudioContext);
          if (!Ctor) return;
          ctx = new Ctor();
          master = ctx.createGain();
          master.gain.value = 1;
          master.connect(ctx.destination);
        }
        // Some browsers suspend the context between gestures; resume() is
        // a no-op if it's already running.
        void ctx.resume();
        scheduleTone(ctx, master!, TONE_SPECS[kind], ctx.currentTime);
      } catch {
        // UI sounds failing is never blocking — swallow.
      }
    },
    destroy() {
      if (ctx) {
        void ctx.close();
        ctx = null;
        master = null;
      }
    },
  };
}
